import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Runes.css';

const Runes = () => {
  const [dailyRune, setDailyRune] = useState(null);
  const [drawnRune, setDrawnRune] = useState(null);
  const [situation, setSituation] = useState('');
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    fetchDailyRune();
  }, []);

  const fetchDailyRune = async () => {
    try {
      const response = await axios.get('/api/runes/daily');
      setDailyRune(response.data);
    } catch (error) {
      console.error('Failed to fetch daily rune:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrawRune = async (e) => {
    e.preventDefault();
    if (!situation.trim()) {
      alert('Kerro ensin elämäntilanteestasi');
      return;
    }

    setDrawing(true);
    try {
      const response = await axios.post('/api/runes/draw', { situation: situation.trim() });
      setDrawnRune(response.data);
      setSituation(''); // Clear situation after drawing
    } catch (error) {
      console.error('Failed to draw rune:', error);
      alert('Riimun nosto epäonnistui. Yritä uudelleen.');
    } finally {
      setDrawing(false);
    }
  };

  return (
    <div className="runes">
      <div className="container">
        <div className="runes-header">
          <h1>Riimut</h1>
          <p className="runes-subtitle">
            Kokeile muinaisen riimu-oraakkelin viisautta ja löydä ohjeita elämäntilanteeseesi
          </p>
        </div>

        {loading ? (
          <div className="runes-loading">
            <div className="moon-loader">
              <div className="moon"></div>
              <p>Ladataan päivän riimua...</p>
            </div>
          </div>
        ) : (
          <>
            {dailyRune && (
              <section className="daily-rune-section">
                <h2 className="section-title">Päivän Riimu</h2>
                <div className="rune-display card">
                  <div className="rune-header">
                    <div className="rune-symbol">{dailyRune.rune.symbol}</div>
                    <h3 className="rune-name">{dailyRune.rune.nameFi}</h3>
                    <div className="rune-meaning">{dailyRune.rune.meaningFi}</div>
                  </div>
                  <div className="rune-interpretation">
                    <div className="rune-symbolism">
                      <h4>Symboliikka</h4>
                      <p>{dailyRune.symbolism}</p>
                    </div>
                    <div className="rune-advice">
                      <h4>Neuvo</h4>
                      <p>{dailyRune.advice}</p>
                    </div>
                    <div className="rune-keywords">
                      <strong>Avainsanat:</strong> {(dailyRune.keywordsFi || dailyRune.keywords || []).join(', ')}
                    </div>
                  </div>
                  <div className="mini-ritual">
                    <h3 className="mini-ritual-title">🕯️ Päivän pieni rituaali (1 min)</h3>
                    <p>Pysähdy ja hengitä 3 kertaa hitaasti.</p>
                    <p>Sano mielessäsi: &lsquo;Näen sen, minkä tämä riimu minussa kirkastaa.&rsquo;</p>
                    <p>Yksi pieni teko: {dailyRune.advice || 'tee yksi asia hitaammin ja tietoisemmin kuin yleensä.'}</p>
                    {((dailyRune.keywordsFi || dailyRune.keywords) && (dailyRune.keywordsFi || dailyRune.keywords).length > 0) && (
                      <p>Avainsana tänään: {(dailyRune.keywordsFi || dailyRune.keywords)[0]}</p>
                    )}
                  </div>
                  <div className="rune-date">
                    Päivämäärä: {new Date(dailyRune.date).toLocaleDateString('fi-FI')}
                  </div>
                </div>
              </section>
            )}

            <section className="draw-rune-section">
              <h2 className="section-title">Nosta oma riimusi</h2>
              <div className="rune-question-card card">
                <p className="question-instruction">
                  Kerro elämäntilanteestasi alle, ja nostamme riimun, joka peilaa sitä mitä jo tiedät – ja tekee viestin näkyväksi.
                </p>
                <form onSubmit={handleDrawRune} className="rune-question-form">
                  <textarea
                    className="question-input"
                    placeholder="Kirjoita elämäntilanteestasi… (esim. Olen kahden vaiheilla uran tai suhteen suhteen)"
                    value={situation}
                    onChange={(e) => setSituation(e.target.value)}
                    rows="4"
                    required
                  />
                  <button 
                    type="submit" 
                    className="btn btn-primary draw-rune-btn"
                    disabled={drawing}
                  >
                    {drawing ? 'Nostetaan riimua...' : 'Nosta riimu →'}
                  </button>
                  <p className="rune-draw-helper">
                    Riimu ei päätä puolestasi – se auttaa sinua kuulemaan itsesi.
                  </p>
                </form>
              </div>

              {drawnRune && (
                <div className="drawn-rune-display card">
                  <div className="rune-header">
                    <div className="rune-symbol">{drawnRune.rune.symbol}</div>
                    <h3 className="rune-name">{drawnRune.rune.nameFi}</h3>
                    <div className="rune-meaning">{drawnRune.rune.meaningFi}</div>
                  </div>
                  {drawnRune.situation && (
                    <div className="rune-situation">
                      <strong>Elämäntilanteesi:</strong> "{drawnRune.situation}"
                    </div>
                  )}
                  <div className="rune-interpretation">
                    <div className="rune-symbolism">
                      <h4>Symboliikka</h4>
                      <p>{drawnRune.symbolism}</p>
                    </div>
                    <div className="rune-advice">
                      <h4>Neuvo elämäntilanteeseesi</h4>
                      <p>{drawnRune.advice}</p>
                    </div>
                    <div className="rune-keywords">
                      <strong>Avainsanat:</strong> {drawnRune.keywords.join(', ')}
                    </div>
                  </div>
                  <div className="mini-ritual">
                    <h3 className="mini-ritual-title">🕯️ Päivän pieni rituaali (1 min)</h3>
                    <p>Pysähdy ja hengitä 3 kertaa hitaasti.</p>
                    <p>Sano mielessäsi: &lsquo;Näen sen, minkä tämä riimu minussa kirkastaa.&rsquo;</p>
                    <p>Yksi pieni teko: {drawnRune.advice || 'tee yksi asia hitaammin ja tietoisemmin kuin yleensä.'}</p>
                    {((drawnRune.keywordsFi || drawnRune.keywords) && (drawnRune.keywordsFi || drawnRune.keywords).length > 0) && (
                      <p>Avainsana tänään: {(drawnRune.keywordsFi || drawnRune.keywords)[0]}</p>
                    )}
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Runes;
